import {hoistCmp, MenuItemLike, uses} from '@xh/hoist/core';
import {AccountStructureModel, NodeSelectOption} from './AccountStructureModel';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {cytoGraph} from '../../../cmp/cytoGraph/CytoGraph';
import {CtxMenuArgs} from '../../../cmp/cytoGraph/CytoGraphTypes';
import {Icon} from '@xh/hoist/icon';
import {getDefaultLayout} from '../../../cmp/cytoGraph/CytoGraphDefaults';
import {getNodeHtmlStyles} from './NodeStyles';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {button} from '@xh/hoist/desktop/cmp/button';
import {select} from '@xh/hoist/desktop/cmp/input';
import {hbox, hspacer} from '@xh/hoist/cmp/layout';
import {AccountStructureItemType} from '../../../data/DataTypes';
import classNames from 'classnames';
import './AccountStructure.scss';
import {buildingColumnsIcon, cabinetFilingIcon, gridRound4Icon, maximizeIcon} from '../../../../core/Icons';

export const accountStructure = hoistCmp.factory<AccountStructureModel>({
    model: uses(AccountStructureModel),
    render({model}) {
        return panel({
            tbar: tbar(),
            item: cytoGraph({
                graphModelFn: graphModel => (model.graphModelRef = graphModel),
                elements: model.elements,
                layout: getDefaultLayout(),
                nodeHtmlStyles: getNodeHtmlStyles(),
                showBackground: model.showBackground,
                onNodeClickFn: id => model.onNodeSelect(id),
                onNodeDblClickFn: id => model.onNodeAction(id),
                onNodeMoveFn: args => model.onNodeMove(args),
                onEdgeDrawnFn: args => model.createEdge(args),
                graphContextMenuItemsFn: args => getGraphMenuItems(args, model),
                nodeContextMenuItemsFn: args => getNodeMenuItems(args, model),
                edgeContextMenuItemsFn: args => getEdgeMenuItems(args, model)
            }),
            mask: model.apiTask
        });
    }
});

const tbar = hoistCmp.factory<AccountStructureModel>(({model}) =>
    toolbar({
        compact: false,
        items: [
            button({icon: Icon.reset(), tooltip: 'Reset View', onClick: () => model.resetView()}),
            button({
                icon: Icon.treeGraph({prefix: 'fal'}),
                tooltip: 'Reset Layout',
                onClick: () => model.resetLayout()
            }),
            button({
                icon: gridRound4Icon(),
                tooltip: 'Show/Hide background',
                active: model.showBackground,
                onClick: () => model.toggleBackground()
            }),
            button({
                icon: maximizeIcon(),
                tooltip: 'Expand All',
                onClick: () => model.expandAll()
            }),
            select({
                bind: 'searchNodeId',
                options: model.searchItems,
                hideDropdownIndicator: true,
                hideSelectedOptionCheck: true,
                placeholder: 'Jump to...',
                menuWidth: 300,
                optionRenderer: (opt: NodeSelectOption) =>
                    hbox({
                        alignItems: 'center',
                        className: classNames('account-structure__search-item', opt.type),
                        items: [
                            opt.type == AccountStructureItemType.PORTFOLIO
                                ? Icon.portfolio()
                                : opt.type == AccountStructureItemType.BROKERAGE
                                  ? buildingColumnsIcon()
                                  : cabinetFilingIcon(),
                            hspacer(10),
                            opt.label
                        ]
                    }),
                filterFn: (opt, filter) => {
                    if (!filter) return true;
                    filter = filter.trim().toUpperCase();
                    return opt.label?.toUpperCase().includes(filter);
                }
            })
        ]
    })
);

function getGraphMenuItems(args: CtxMenuArgs, model: AccountStructureModel): MenuItemLike[] {
    return [
        {icon: Icon.reset(), text: 'Reset view', actionFn: () => model.resetView()},
        {icon: Icon.refresh(), text: 'Auto layout', actionFn: () => model.resetLayout()},
        '-',
        {
            icon: Icon.add(),
            text: 'Add...',
            items: [
                {text: 'Brokerage', icon: buildingColumnsIcon(), intent: 'success', actionFn: () => model.addBrokerageNode(args)},
                {text: 'Portfolio', icon: Icon.portfolio(), intent: 'warning', actionFn: () => model.addPortfolioNode(args)},
                {text: 'Custom', icon: cabinetFilingIcon(), intent: 'primary', actionFn: () => model.addCustomNode(args)}
            ]
        }
    ];
}

function getNodeMenuItems(args: CtxMenuArgs, model: AccountStructureModel): MenuItemLike[] {
    return [{icon: Icon.delete(), text: 'Delete Entity', actionFn: () => model.deleteNode(args)}];
}

function getEdgeMenuItems(args: CtxMenuArgs, model: AccountStructureModel): MenuItemLike[] {
    return [{icon: Icon.delete(), text: 'Delete Edge', actionFn: () => model.deleteEdge(args)}];
}
